/*
  OK FBI — Phase III Slice A: Investigator Profile · Badge Collection
  Route: /investigator/:userId

  Displays the FULL earned-badge collection (no +N limit) plus per-badge
  metadata resolved against the public catalog. Hidden-until-earned
  badges stay hidden until earned. Uploaded badge artwork appears here
  automatically because the profile reads the same catalog the card
  system uses.
*/
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { assetUrl } from "@/contexts/SessionContext";
import { Shell } from "@/components/Shell";
import { Award, Trophy, Target, Fingerprint, CheckCircle2, Calendar, ShieldCheck, User } from "lucide-react";
import { InvestigatorCard } from "@/components/InvestigatorCard";
import { useAuth } from "@/pages/AuthPage";

// The Profile Card uses the same public template/layout contract as the
// OWNER Card Layout Editor. Profile-specific identity values remain dynamic
// participant data and are never written into the template.
function ProfileCardHero({ card, template, participant }) {
  if (!card || !template) return null;
  return (
    <div className="mb-6 overflow-hidden" data-testid="profile-card-hero"
         data-card-source={card.source || "default"}
         data-plan={card.plan || ""}>
      <InvestigatorCard template={template} participant={participant} size="lg"/>
    </div>
  );
}

function ProfileIdentity({ profile, isSelf }) {
  const avatar = profile?.avatar_url ? assetUrl(profile.avatar_url) : "";
  const initial = (profile?.name || "OK").slice(0, 1);
  const rank = profile?.rank_name || profile?.rank?.name || "rookie";
  const xp = Number(profile?.xp || 0);
  const level = Number(profile?.level || 1);
  const nextLevelXp = level * 3000;
  const progressPct = Math.min(100, Math.max(5, Math.round(((xp % 3000) / 3000) * 100)));

  return (
    <section className="mb-6 border border-neutral-900 rounded-xl bg-neutral-950/60 p-5 relative overflow-hidden"
             data-testid="profile-identity">
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-16 h-16 md:w-20 md:h-20 shrink-0 rounded-full overflow-hidden border-2 border-yellow-600/70 bg-neutral-900 flex items-center justify-center text-yellow-300 font-cairo text-2xl font-bold shadow-lg shadow-black/60">
          {avatar ? (
            <img src={avatar} alt="" className="w-full h-full object-cover" data-testid="profile-avatar"/>
          ) : initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-cairo font-black text-2xl text-yellow-200 truncate" data-testid="profile-name">
              {profile?.name || "محقق"}
            </h2>
            {isSelf && (
              <span className="px-2 py-0.5 rounded text-[10px] font-cairo bg-yellow-500/15 border border-yellow-500/40 text-yellow-300">
                ملفك الشخصي
              </span>
            )}
            <span className="px-2 py-0.5 rounded text-[10px] font-cairo bg-neutral-900 border border-neutral-800 text-neutral-400">
              {profile?.membership_plan || "FREE"}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs font-mono text-neutral-400">
            <span data-testid="profile-agent-id" className="text-yellow-400/90 font-bold">Agent #{profile?.agent_id || "—"}</span>
            <span data-testid="profile-rank" className="text-emerald-400">{rank}</span>
            <span className="text-neutral-500">·</span>
            <span>النقاط: <b className="text-yellow-200">{Number(profile?.points || 0).toLocaleString("en-US")}</b></span>
            <span className="text-neutral-500">·</span>
            <span>المستوى: <b className="text-yellow-200">{level}</b></span>
          </div>

          {/* Progress bar */}
          <div className="mt-3 max-w-md">
            <div className="flex justify-between text-[10px] font-mono text-neutral-500 mb-1">
              <span>XP: {xp.toLocaleString("en-US")}</span>
              <span>المستوى التالي: {nextLevelXp.toLocaleString("en-US")} XP</span>
            </div>
            <div className="w-full h-1.5 bg-neutral-900 border border-neutral-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-l from-yellow-500 to-amber-600 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* TikTok Connection Status */}
          <div className="flex flex-wrap items-center gap-2 mt-3.5 text-xs font-cairo">
            {profile?.tiktok_linked ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-950/40 border border-emerald-800/60 text-emerald-300 font-mono-ar text-[11px]" data-testid="profile-tiktok-status">
                <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current text-cyan-400">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.46V11.8a8.28 8.28 0 0 0 4.77 1.5V9.86a4.87 4.87 0 0 1-1-.17v-3z"/>
                </svg>
                <span>حساب TikTok مرتبط {profile.tiktok_username ? `(@${profile.tiktok_username})` : ""}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-neutral-900/60 border border-neutral-800 text-neutral-500 text-[11px]" data-testid="profile-tiktok-status">
                <span>حساب TikTok: غير مرتبط</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function BadgeTile({ def, earned }) {
  const has = Boolean(earned);
  const dim = def?.hidden_until_earned && !has;
  if (dim) return null;
  return (
    <div className={`border rounded-lg p-3 flex items-center gap-3 ${
      has ? "border-yellow-800/70 bg-yellow-950/20" : "border-neutral-900 bg-neutral-950/40 opacity-70"
    }`}
         data-testid={`profile-badge-${def.id}`}>
      <div className="w-14 h-14 rounded-md border border-neutral-800 bg-neutral-950 shrink-0"
           style={def.artwork_url ? {
             backgroundImage: `url("${def.artwork_url}")`,
             backgroundSize: "cover", backgroundPosition: "center",
           } : undefined}>
        {!def.artwork_url && (
          <div className="w-full h-full flex items-center justify-center text-yellow-500/60">
            <Award size={20}/>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className={`font-cairo font-black text-sm truncate ${has ? "text-yellow-200" : "text-neutral-400"}`}>
          {def.name}
        </div>
        {def.description && (
          <div className="text-[11px] font-cairo text-neutral-500 truncate">
            {def.description}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1 text-[10px] font-mono text-neutral-500">
          <span className="border border-neutral-800 px-1.5 py-0.5 rounded">{def.category}</span>
          {has ? (
            <span className="text-emerald-300">مُكتسَبة · {earned.earned_at?.slice(0, 10)}</span>
          ) : (
            <span className="text-neutral-500">لم تُكتسب بعد</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InvestigatorProfilePage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const targetId = userId || user?.user_id;

  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!targetId) {
      setLoading(false);
      return;
    }
    try {
      const [p, c] = await Promise.all([
        api.get(`/investigator/${targetId}/profile`),
        api.get(`/badges/catalog`),
      ]);
      setProfile(p.data);
      setCatalog(c.data.badges || []);
    } catch (e) {
      /* silent */
    } finally { setLoading(false); }
  }, [targetId]);

  useEffect(() => { document.title = "OK FBI · ملف المحقق"; load(); }, [load]);

  const earnedById = {};
  for (const a of profile?.awards || []) earnedById[a.badge_id] = a;

  const visible = catalog.filter(def =>
    def.profile_visible !== false &&
    (!def.hidden_until_earned || earnedById[def.id])
  );

  const stats = profile?.stats || {};
  const isSelf = (!userId || userId === user?.user_id || profile?.agent_id === user?.agent_id);

  // Accuracy calculation
  const accNum = Number(profile?.accuracy_num || 0);
  const accDen = Number(profile?.accuracy_den || 0);
  const accuracyPct = accDen > 0 ? Math.round((100 * accNum) / accDen) : 0;

  return (
    <Shell>
      <div className="min-h-screen p-6 max-w-[1200px] mx-auto" dir="rtl"
           data-testid="investigator-profile">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.35em] text-yellow-500/70 font-cairo">
              OK · FBI · INVESTIGATOR PROFILE
            </div>
            <h1 className="text-2xl font-cairo text-yellow-300 font-black mt-1">
              ملف المحقق
            </h1>
          </div>
          <Link
            to="/leaderboard"
            className="text-xs font-cairo text-neutral-400 hover:text-gold border border-neutral-800 hover:border-gold/40 px-3 py-1.5 rounded transition-colors"
          >
            ← لوحة المتصدرين
          </Link>
        </header>

        {/* Package-driven Profile Card / frame */}
        {profile?.profile_card && profile?.card_template && (
          <ProfileCardHero
            card={profile.profile_card}
            template={profile.card_template}
            participant={{
              display_name: profile.name,
              agent_id: profile.agent_id,
              rank: profile.rank_name || profile.rank?.name,
              points: profile.points,
              avatar_url: profile.avatar_url,
              subscription_label: profile.membership_plan,
              badges: (profile.awards || []).map((award) => {
                const def = catalog.find((item) => item.id === award.badge_id);
                return def ? { label: def.name, icon_url: def.artwork_url, id: def.id } : award.badge_id;
              }),
            }}
          />
        )}
        {profile && <ProfileIdentity profile={profile} isSelf={isSelf} />}

        {loading ? (
          <div className="text-sm text-neutral-500 font-cairo">جارِ التحميل…</div>
        ) : (
          <>
            {/* Stats summary grid — 6 key stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <div className="border border-neutral-900 rounded-lg p-3 bg-neutral-950/60 text-center"
                   data-testid="profile-stat-position">
                <div className="text-[10px] font-cairo text-neutral-500 mb-0.5">الترتيب العالمي</div>
                <div className="text-xl font-cairo text-yellow-200 font-black tabular-nums">
                  #{profile?.position || "—"}
                </div>
              </div>

              <div className="border border-neutral-900 rounded-lg p-3 bg-neutral-950/60 text-center"
                   data-testid="profile-stat-cases_won">
                <div className="text-[10px] font-cairo text-neutral-500 mb-0.5">قضايا محلولة</div>
                <div className="text-xl font-cairo text-yellow-200 font-black tabular-nums">
                  {Number(profile?.solved_cases || stats?.cases_won || 0)}
                </div>
              </div>

              <div className="border border-neutral-900 rounded-lg p-3 bg-neutral-950/60 text-center"
                   data-testid="profile-stat-accuracy">
                <div className="text-[10px] font-cairo text-neutral-500 mb-0.5">نسبة الدقة</div>
                <div className="text-xl font-cairo text-emerald-400 font-black tabular-nums">
                  {accuracyPct}%
                </div>
                {accDen > 0 && (
                  <div className="text-[9px] font-mono text-neutral-500">{accNum}/{accDen}</div>
                )}
              </div>

              <div className="border border-neutral-900 rounded-lg p-3 bg-neutral-950/60 text-center"
                   data-testid="profile-stat-evidence_discovered">
                <div className="text-[10px] font-cairo text-neutral-500 mb-0.5">أدلة مكتَشَفة</div>
                <div className="text-xl font-cairo text-yellow-200 font-black tabular-nums">
                  {Number(stats?.evidence_discovered || 0)}
                </div>
              </div>

              <div className="border border-neutral-900 rounded-lg p-3 bg-neutral-950/60 text-center"
                   data-testid="profile-stat-correct_votes">
                <div className="text-[10px] font-cairo text-neutral-500 mb-0.5">تصويتات صحيحة</div>
                <div className="text-xl font-cairo text-yellow-200 font-black tabular-nums">
                  {Number(stats?.correct_votes || 0)}
                </div>
              </div>

              <div className="border border-neutral-900 rounded-lg p-3 bg-neutral-950/60 text-center"
                   data-testid="profile-stat-subscription_streak_months">
                <div className="text-[10px] font-cairo text-neutral-500 mb-0.5">أشهر الاشتراك</div>
                <div className="text-xl font-cairo text-yellow-200 font-black tabular-nums">
                  {Number(stats?.subscription_streak_months || 0)}
                </div>
              </div>
            </div>

            {/* Badges */}
            <h2 className="text-lg font-cairo text-yellow-300 font-black mb-3 flex items-center gap-2">
              <Award size={16}/> الشارات والإنجازات
              <span className="text-[11px] font-mono text-neutral-500">
                · {profile?.awards?.length || 0} مُكتسَبة من {visible.length} ظاهرة
              </span>
            </h2>

            {visible.length === 0 ? (
              <div className="text-sm font-cairo text-neutral-500">لا توجد شارات معروضة بعد.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3" data-testid="profile-badges-grid">
                {visible.map(def => (
                  <BadgeTile key={def.id}
                             def={def}
                             earned={earnedById[def.id]}/>
                ))}
              </div>
            )}

            {/* Slice 2 · Expired badge history — kept for the record.
                Respects `hide_after_expiry` server-side (already filtered). */}
            {(profile?.history || []).length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-cairo text-neutral-300 font-black mb-3 flex items-center gap-2">
                  <Award size={16} className="opacity-60"/> السجل · شارات انتهت
                  <span className="text-[11px] font-mono text-neutral-500">
                    · {profile.history.length}
                  </span>
                </h2>
                <div className="grid md:grid-cols-2 gap-3" data-testid="profile-history-grid">
                  {profile.history.map(a => {
                    const def = catalog.find(d => d.id === a.badge_id) || { id: a.badge_id, name: a.badge_id, category: "—" };
                    return (
                      <div key={a.id}
                           data-testid={`profile-history-${a.id}`}
                           className="border border-neutral-800 rounded-lg p-3 flex items-center gap-3 bg-neutral-950/40 opacity-70">
                        <div className="w-14 h-14 rounded-md border border-neutral-800 bg-neutral-950 shrink-0 grayscale"
                             style={def.artwork_url ? {
                               backgroundImage: `url("${def.artwork_url}")`,
                               backgroundSize: "cover", backgroundPosition: "center",
                             } : undefined}>
                          {!def.artwork_url && (
                            <div className="w-full h-full flex items-center justify-center text-neutral-500">
                              <Award size={20}/>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-cairo font-black text-sm text-neutral-300 truncate">{def.name}</div>
                          <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                            حصل: {a.earned_at?.slice(0,10)} · انتهى: {a.expires_at?.slice(0,10)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}
